import { useT as useDesignT, type Catalogue } from '@kkf/design'
import EN from './locales/en.json'

/** Every key in the catalogue. Each locale file is checked against it by `npm run audit:i18n`. */
export type MessageKey = keyof typeof EN

/** English, statically imported: it is the fallback for every key in every language. */
export const ENGLISH: Catalogue = EN

/**
 * The other locales, lazily.
 *
 * Vite turns this glob into one chunk per locale, so a first visit downloads one language
 * rather than all of them. `en.json` is excluded deliberately — it is statically imported
 * above and must be in the initial bundle; importing it both ways makes Rollup warn and
 * then keep it in the main chunk anyway.
 */
const GLOB = import.meta.glob<{ default: Catalogue }>(['./locales/*.json', '!./locales/en.json'])

export const LOADERS = {
  mn: GLOB['./locales/mn.json'],
}

/**
 * A typed `t`, over the design system's untyped one.
 *
 * The design system cannot know this app's keys — five apps, five sets of strings — so its
 * `t` takes a `string`. This narrows it, so a typo is a compile error rather than the key
 * itself appearing on the page.
 *
 * `values` interpolates `{name}` placeholders. Deliberately not a formatting library: there
 * is no pluralisation, no dates and no currency in this catalogue.
 */
export function useT(): (key: MessageKey, values?: Record<string, string | number>) => string {
  const t = useDesignT()
  return (key, values) => {
    const text = t(key)
    if (!values) return text
    return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
      // An unknown placeholder is left visible rather than blanked: `{contry}` on the page
      // is a typo somebody reports, and an empty gap is one nobody sees.
      name in values ? String(values[name]) : whole,
    )
  }
}
